import { type CSSProperties, type ReactNode } from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';

function parseSide(
  placement: string,
): 'top' | 'bottom' | 'left' | 'right' {
  const first = (placement || 'bottom').split(' ')[0];
  if (first === 'top' || first === 'left' || first === 'right') return first;
  return 'bottom';
}

function parseAlign(placement: string): 'start' | 'center' | 'end' {
  const second = (placement || '').split(' ')[1];
  if (second === 'start') return 'start';
  if (second === 'end') return 'end';
  return 'center';
}

type PopoverProps = {
  triggerRef: React.RefObject<HTMLElement>;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: string;
  offset?: number;
  isNonModal?: boolean;
  shouldCloseOnInteractOutside?: (element: Element) => boolean;
  style?: CSSProperties;
  children?: ReactNode;
  [key: string]: unknown;
};

export const Popover = ({
  triggerRef,
  isOpen = false,
  onOpenChange,
  placement = 'bottom end',
  offset = 4,
  isNonModal,
  shouldCloseOnInteractOutside,
  style,
  children,
  ...rest
}: PopoverProps) => {
  return (
    <PopoverPrimitive.Root
      open={isOpen}
      onOpenChange={onOpenChange}
      modal={!isNonModal}
    >
      <PopoverPrimitive.Anchor
        virtualRef={triggerRef as React.RefObject<{ getBoundingClientRect(): DOMRect }>}
      />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={parseSide(placement)}
          align={parseAlign(placement)}
          sideOffset={offset}
          onOpenAutoFocus={isNonModal ? e => e.preventDefault() : undefined}
          onInteractOutside={e => {
            if (
              shouldCloseOnInteractOutside &&
              !shouldCloseOnInteractOutside(e.target as Element)
            ) {
              e.preventDefault();
            }
          }}
          style={{
            padding: 0,
            userSelect: 'none',
            border: '1px solid var(--border)',
            borderRadius: 8,
            backgroundColor: 'var(--popover)',
            color: 'var(--popover-foreground)',
            boxShadow:
              '0 12px 28px -8px rgba(0, 0, 0, 0.35), 0 4px 8px -4px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            zIndex: 9999,
            ...style,
          }}
          {...rest}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

